import { chakra, Button, Image } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React from 'react';
import { useConnect, useAccount, useDisconnect } from 'wagmi';

import type { WalletType } from 'types/client/wallets';

import shortenString from 'lib/shortenString';
import useWeb3AccountWithDomain from 'lib/web3/useAccountWithDomain';
import { IconButton } from 'toolkit/chakra/icon-button';
import IconSvg from 'ui/shared/IconSvg';
import WalletSelectorModal from 'ui/shared/WalletSelectorModal';
import UserWalletMenuContent from 'ui/snippets/user/wallet/UserWalletMenuContent';

import { HoverCardContent, HoverCardRoot, HoverCardTrigger } from '../../../../toolkit/chakra/hover-card';

interface Props {
  compact?: boolean;
}

const ConnectWalletNavItem = ({ compact }: Props) => {
  const router = useRouter();
  const [ isModalOpen, setIsModalOpen ] = React.useState(false);
  const { connect, connectors } = useConnect();
  const { isConnected, address, connector } = useAccount();
  const { disconnect } = useDisconnect();
  const { domain } = useWeb3AccountWithDomain(isConnected);

  const handleSelectWallet = React.useCallback((wallet: WalletType, provider: any) => {
    setIsModalOpen(false);

    // Find matching connector
    const connector = connectors.find(c => {
      const id = c.id.toLowerCase();
      const name = c.name.toLowerCase();
      const target = wallet.toLowerCase();

      // Direct ID match
      if (id === target) return true;

      // Name match
      if (name === target) return true;

      // Specific mappings
      if (target === 'metamask' && (name.includes('metamask') || id.includes('metamask'))) return true;
      if (target === 'coinbase' && (id === 'coinbasewallet' || name.includes('coinbase'))) return true;
      if (target === 'trust' && (name.includes('trust') || id.includes('trust'))) return true;
      if (target === 'rabby' && (name.includes('rabby') || id.includes('rabby'))) return true;
      if (target === 'okx' && (name.includes('okx') || id.includes('okx'))) return true;
      if (target === 'token_pocket' && (name.includes('token') || id.includes('token'))) return true;

      return false;
    });

    if (connector) {
      connect({ connector });
    } else {
      console.error('Connector not found for wallet:', wallet);
      // Fallback: try to find injected if generic
      const injected = connectors.find(c => c.id === 'injected');
      if (injected) {
        connect({ connector: injected });
      }
    }
  }, [ connect, connectors ]);

  const handleDisconnect = React.useCallback(() => {
    disconnect();
  }, [ disconnect ]);

  const handleOpenWallet = React.useCallback(() => {
    setIsModalOpen(true);
  }, []);

  if (isConnected && address) {
    return (
      <chakra.div>
        <HoverCardRoot openDelay={ 0 } closeDelay={ 100 } positioning={{ placement: 'bottom-end' }}>
          <HoverCardTrigger>
            { compact ? (
              <IconButton
                variant="ghost"
                size="2xs"
                borderRadius="lg"
                color="#FF8C00"
                aria-label="Wallet account"
                onClick={ () => router.push(`/address/${ address }` as any) }
              >
                { connector?.icon ? <Image src={ connector.icon } alt="Wallet Logo"/> : <IconSvg name="wallet"/> }
              </IconButton>
            ) : (
              <Button
                variant="ghost"
                px={ 4 }
                py={ 2 }
                textStyle="sm"
                fontWeight={ 600 }
                borderRadius="lg"
                color="#FF8C00"
                transition="all 0.2s"
                _hover={{
                  textDecoration: 'none',
                  bg: { base: 'gray.100', _dark: 'whiteAlpha.100' },
                  transform: 'translateY(-1px)',
                }}
                _active={{
                  bg: { base: 'gray.200', _dark: 'whiteAlpha.200' },
                }}
                onClick={ () => router.push(`/address/${ address }` as any) }
              >
                { connector?.icon && <Image src={ connector.icon } boxSize="20px" mr={ 2 } alt="Wallet Logo"/> }
                { domain || shortenString(address) }
              </Button>
            ) }
          </HoverCardTrigger>
          <HoverCardContent w="235px" bg="black" color="white" border="1px solid" borderColor="whiteAlpha.300">
            <chakra.div p={ 4 }>
              <UserWalletMenuContent
                address={ address }
                domain={ domain }
                onDisconnect={ handleDisconnect }
                onOpenWallet={ handleOpenWallet }
              />
            </chakra.div>
          </HoverCardContent>
        </HoverCardRoot>
        <WalletSelectorModal
          open={ isModalOpen }
          onOpenChange={ (details) => setIsModalOpen(details.open) }
          onSelect={ handleSelectWallet }
        />
      </chakra.div>
    );
  }

  return (
    <>
      <chakra.div>
        { compact ? (
          <IconButton
            variant="ghost"
            size="2xs"
            borderRadius="lg"
            color={{ base: 'gray.600', _dark: 'whiteAlpha.800' }}
            aria-label="Connect wallet"
            onClick={ () => setIsModalOpen(true) }
          >
            <IconSvg name="wallet"/>
          </IconButton>
        ) : (
          <chakra.button
            display="flex"
            alignItems="center"
            w="fit-content"
            px={ 4 }
            py={ 2 }
            textStyle="sm"
            fontWeight={ 600 }
            borderRadius="lg"
            color={{ base: 'gray.600', _dark: 'whiteAlpha.800' }}
            transition="all 0.2s"
            _hover={{
              textDecoration: 'none',
              bg: { base: 'gray.100', _dark: 'whiteAlpha.100' },
              color: '#FF8C00',
              transform: 'translateY(-1px)',
            }}
            onClick={ () => setIsModalOpen(true) }
          >
            Connect Wallet
          </chakra.button>
        ) }
      </chakra.div>
      <WalletSelectorModal
        open={ isModalOpen }
        onOpenChange={ (details) => setIsModalOpen(details.open) }
        onSelect={ handleSelectWallet }
      />
    </>
  );
};

export default React.memo(ConnectWalletNavItem);
